/* eslint-disable @typescript-eslint/no-explicit-any */
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useI18n } from 'hooks/useI18n';
import { matchSorter } from 'match-sorter';
import { forwardRef, ReactNode, useEffect, useMemo, useState } from 'react';
import Select, { ActionMeta, components, createFilter } from 'react-select';
import { DEFAULT_CUSTOM_SELECT_MATCH_ORDER } from './CurstomSelect.constants';

export type CustomSelectOption = {
  label: string | React.ReactNode;
  value: string | number;
  title?: string;
};

export type CustomSelectProps = {
  options: CustomSelectOption[];
  placeholder?: string | null;
  isMulti?: boolean;
  id: string;
  isSmall?: boolean;
  styles?: any;
  defaultValue?: any;
  isTransparent?: boolean;
  value?: any;
  matchOrder?: boolean;
  max?: number;
  hideDropdownUntilSearch?: boolean;
  formatOptionLabel?: (label: string) => ReactNode;
  variant?: 'normal' | 'error';
  closeMenuOnSelect?: boolean;
  onChange?: ((value: any, actionMeta?: ActionMeta<any>) => void) &
    ((value: any, action: ActionMeta<any>) => void);
  menuPosition?: 'absolute' | 'fixed';
  matchFrom?: 'any' | 'start';
  multiValueSeparator?: string;
};

const Menu = (props: any): React.ReactElement => {
  return <components.Menu {...props}>{props.children}</components.Menu>;
};

const MultiValueSeparator = (props: any): React.ReactElement => {
  return (
    <>
      <components.MultiValue {...props}>{props.children}</components.MultiValue>
      {props.multiValueSeparator
        ? props.selectProps.value.length - 1 > props.index && (
            <span className="multi-value-separator">
              {props.multiValueSeparator}
            </span>
          )
        : ''}
    </>
  );
};

const ClearIndicator = (props: any): React.ReactElement => {
  return (
    <components.ClearIndicator {...props}>
      <FontAwesomeIcon icon={faTimes} />
    </components.ClearIndicator>
  );
};

const CustomSelect = forwardRef<any, CustomSelectProps>(
  (
    {
      options,
      placeholder,
      isMulti = false,
      isSmall = false,
      id,
      styles,
      defaultValue,
      isTransparent = false,
      onChange,
      value,
      matchOrder = DEFAULT_CUSTOM_SELECT_MATCH_ORDER,
      max,
      hideDropdownUntilSearch,
      formatOptionLabel,
      variant = 'normal',
      closeMenuOnSelect = true,
      menuPosition = 'absolute',
      matchFrom = 'any',
      multiValueSeparator
    }: CustomSelectProps,
    ref
  ) => {
    const { t } = useI18n('common');
    const [currentOptions, setCurrentOptions] = useState(options);

    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const handleOnInputChange = (newValue: string): void => {
      if (hideDropdownUntilSearch) {
        setMenuIsOpen(newValue.length > 0);
      }

      if (matchOrder) {
        setCurrentOptions(matchSorter(options, newValue, { keys: ['label'] }));
      }
    };

    const MultiValueWithSeparator = useMemo(
      // eslint-disable-next-line react/display-name
      () => (props: any) =>
        (
          <MultiValueSeparator
            {...props}
            multiValueSeparator={multiValueSeparator}
          />
        ),
      [multiValueSeparator]
    );

    useEffect(() => {
      setCurrentOptions(options);
    }, [options]);

    return (
      <Select
        aria-label="id"
        options={currentOptions}
        classNamePrefix="mf-select"
        className={`mf-select ${
          hideDropdownUntilSearch ? 'mf-select-autocomplete' : ''
        } ${isSmall ? 'cs-sm' : ''} ${isTransparent ? 'cs-transparent' : ''}
        ${variant === 'error' ? 'cs-error' : ''}`}
        placeholder={placeholder}
        isMulti={isMulti}
        inputId={id}
        styles={styles}
        defaultValue={defaultValue}
        menuPortalTarget={
          typeof window !== 'undefined'
            ? document.querySelector('body')
            : undefined
        }
        formatOptionLabel={
          formatOptionLabel ? (val) => formatOptionLabel(val.label) : undefined
        }
        onChange={(newValue, actionMeta) => {
          // Prevent onChange from firing when removing items with backspace
          if (actionMeta.action === 'pop-value') return;

          onChange?.(newValue, actionMeta);
        }}
        noOptionsMessage={() => t('customSelect.noOptions')}
        {...(!matchOrder && {
          filterOption: createFilter({
            ignoreAccents: true,
            ignoreCase: true,
            matchFrom,
            trim: true
          })
        })}
        value={value}
        max={max}
        ref={ref}
        menuPosition={menuPosition}
        onInputChange={handleOnInputChange}
        closeMenuOnSelect={closeMenuOnSelect}
        menuIsOpen={hideDropdownUntilSearch ? menuIsOpen : undefined}
        components={
          hideDropdownUntilSearch
            ? {
                Menu,
                ClearIndicator,
                MultiValue: MultiValueWithSeparator,
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null
              }
            : {
                Menu,
                ClearIndicator,
                MultiValue: MultiValueWithSeparator
              }
        }
      />
    );
  }
);

CustomSelect.displayName = 'CustomSelect';
export default CustomSelect;
